import { useCallback, useContext, useEffect } from "react";
import { getUser, getIntercomToken } from "~/api/account/requests";
import { AccountContext } from "~/contexts/Account";
import { IAccount } from "~/types/account";
import { Userpilot } from "userpilot";
import _debounce from "lodash/debounce";
import { useIntercom } from "react-use-intercom";
import { intercomKey } from "~/helpers/environment";
import { DISABLE_PAYMENTS } from "~/helpers/plans";
import { useStore } from "~/hooks/useStore";

let intercomBootedForAccountId: string | null = null;
let intercomJwtRefreshInterval: ReturnType<typeof setInterval> | null = null;

const INTERCOM_JWT_REFRESH_INTERVAL_MS = 45 * 60 * 1000; // 45 min, under the 1h expiry

export const useAccount = () => {
  const { account, setAccount } = useContext(AccountContext);
  const { boot, update } = useIntercom();
  const { store } = useStore();
  const APP_IDENTIFIER = "smartli-v2-app";

  const bootIntercomForAccount = async (account: IAccount) => {
    if (!intercomKey || !account.id) {
      return;
    }
    if (intercomBootedForAccountId === account.id) {
      return;
    }
    intercomBootedForAccountId = account.id;

    let intercom_user_jwt: string;
    try {
      ({ intercom_user_jwt } = await getIntercomToken());
    } catch (error) {
      intercomBootedForAccountId = null;
      console.error("Failed to fetch Intercom token", error);
      return;
    }

    boot({
      email: account.email,
      userId: `smartli_${account.id.toString()}`,
      name: account.name,
      intercomUserJwt: intercom_user_jwt,
      customAttributes: {
        app: APP_IDENTIFIER,
        shop_name: store?.name,
        shop_url: store?.url,
        shop_email: store?.email,
        shop_phone: store?.phone,
      },
    });

    if (intercomJwtRefreshInterval) {
      clearInterval(intercomJwtRefreshInterval);
    }
    intercomJwtRefreshInterval = setInterval(async () => {
      try {
        const { intercom_user_jwt: freshJwt } = await getIntercomToken();
        update({ intercomUserJwt: freshJwt });
      } catch (error) {
        console.error("Failed to refresh Intercom token", error);
      }
    }, INTERCOM_JWT_REFRESH_INTERVAL_MS);
  };

  const bootIntercom = () => (account ? bootIntercomForAccount(account) : null);

  const setAccountWithThirdParties = (acc: IAccount | null) => {
    setAccount(acc);

    if (!acc) {
      if (intercomJwtRefreshInterval) {
        clearInterval(intercomJwtRefreshInterval);
        intercomJwtRefreshInterval = null;
      }
      intercomBootedForAccountId = null;
      return;
    }

    if (acc?.id) {
      Userpilot.identify(acc.id.toString(), {
        email: acc.email,
        created_at: acc.created_at,
        name: acc.name,
        payment_provider: acc.payment_provider,
        signup_origin: acc.signup_origin,
      });

      // Microsoft Clarity - attach the user's email to the recorded session.
      // The friendly-name (5th arg) is stored unmasked; the custom-id is hashed by Clarity.
      window.clarity?.("identify", acc.email, null, null, acc.email);
      window.clarity?.("set", "user_email", acc.email);
      if (acc.active_subscription || DISABLE_PAYMENTS) {
        bootIntercomForAccount(acc);
      }
    }
  };

  const getAccount = async () => {
    const profile = (await getUser()) as IAccount;
    setAccountWithThirdParties(profile);
    return profile;
  };
  const debouncedGetAccount = useCallback(_debounce(getAccount, 200), []);

  useEffect(() => {
    if (!account) debouncedGetAccount();
  }, []);

  return {
    account,
    setAccount: setAccountWithThirdParties,
    getAccount,
    refetch: getAccount,
    bootIntercom,
  };
};
